import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface PreviewFrameProps {
    children: ReactNode
    className?: string
}

/** The app surface every feature preview sits on. */
export const PreviewFrame: FC<PreviewFrameProps> = ({ children, className }): ReactNode => {
    return (
        <div
            className={cn(
                'relative rounded-[22px] border border-burgundy/10 bg-paper p-4 text-left text-burgundy shadow-[0_50px_100px_-50px_rgb(0_0_0/0.55)] sm:p-5',
                className
            )}
        >
            {children}
        </div>
    )
}

interface FloatingCardProps {
    children: ReactNode
    className?: string
    slow?: boolean
}

/** Small interface element that floats in front of a preview for depth. */
export const FloatingCard: FC<FloatingCardProps> = ({ children, className, slow = false }): ReactNode => {
    return (
        <div
            aria-hidden='true'
            className={cn(
                'pointer-events-none absolute z-10 hidden rounded-2xl border border-burgundy/10 bg-ivory/95 p-3 text-left text-burgundy shadow-[0_30px_60px_-24px_rgb(0_0_0/0.45)] backdrop-blur sm:block',
                slow ? 'animate-float-slow' : 'animate-float',
                className
            )}
        >
            {children}
        </div>
    )
}

interface SegmentedProps<T extends string> {
    label: string
    value: T
    options: Array<{ value: T, label: string }>
    onChange: (value: T) => void
    className?: string
}

export const Segmented = <T extends string>({
    label,
    value,
    options,
    onChange,
    className
}: SegmentedProps<T>): ReactNode => {
    return (
        <div role='group' aria-label={label} className={cn('inline-flex rounded-full border border-burgundy/10 bg-ivory p-0.5', className)}>
            {options.map(option => (
                <button
                    key={option.value}
                    type='button'
                    aria-pressed={value === option.value}
                    onClick={() => onChange(option.value)}
                    className={cn(
                        'rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors duration-300',
                        value === option.value ? 'bg-burgundy text-cream' : 'text-stone-dark hover:text-burgundy'
                    )}
                >
                    {option.label}
                </button>
            ))}
        </div>
    )
}
